/**
 * Header "Product Categories" button — sits between the notification bell and
 * the profile gear. Opens a dialog listing every product with a margin
 * category (A = Low, B = Moderate, C = High), grouped by category with a
 * category filter + search. Visible to roles 2/3/4/5/13/14/17 — NOT role 1
 * (Shailendra, 2026-09-30). See utils/marginCategory.js.
 */
import React, { useEffect, useMemo, useState } from 'react';

// material-ui
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar, Box, ButtonBase, Chip, CircularProgress, Dialog, DialogContent, DialogTitle,
  IconButton, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

// assets
import { IconCategory } from '@tabler/icons';

import { getDecryptDataFromLocal } from '../../../utils/encrypt';
import {
  MARGIN_CATEGORIES, MARGIN_CATEGORY_ORDER_HIGH_FIRST, MarginCategoryHeaderRow,
  fetchMarginCategoryProducts, groupByMarginCategory,
} from '../../../utils/marginCategory';

const VISIBLE_ROLES = [2, 3, 4, 5, 13, 14, 17];

const useStyles = makeStyles((theme) => ({
  headerAvatar: {
    ...theme.typography.commonAvatar,
    ...theme.typography.mediumAvatar,
    transition: 'all .2s ease-in-out',
    background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
    color: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
    '&:hover': {
      background: theme.palette.mode === 'dark' ? theme.palette.secondary.main : theme.palette.secondary.dark,
      color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.light,
    },
  },
}));

const fmtMoney = (n) => (n == null ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

function ProductCategoryDialog({ open, onClose }) {
  const [products, setProducts] = useState(null);
  const [catFilter, setCatFilter] = useState('ALL');
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || products) return;
    fetchMarginCategoryProducts().then(setProducts);
  }, [open, products]);

  const catMap = useMemo(() => new Map((products || []).map(p => [String(p._id), p.marginCategory])), [products]);
  const counts = useMemo(() => {
    const c = { ALL: (products || []).length };
    (products || []).forEach(p => { c[p.marginCategory] = (c[p.marginCategory] || 0) + 1; });
    return c;
  }, [products]);

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (products || []).filter(p =>
      (catFilter === 'ALL' || p.marginCategory === catFilter) &&
      (!q || `${p.name} ${p.brand}`.toLowerCase().includes(q)));
    return groupByMarginCategory(list, p => p._id, catMap, MARGIN_CATEGORY_ORDER_HIGH_FIRST);
  }, [products, catFilter, search, catMap]);

  const filterChip = (key, label, cat) => {
    const active = catFilter === key;
    return (
      <Chip
        key={key}
        label={`${label} (${counts[key] || 0})`}
        onClick={() => setCatFilter(key)}
        style={{
          fontWeight: 600,
          background: active ? (cat ? cat.bg : '#424242') : '#f5f5f5',
          color: active ? (cat ? cat.color : '#fff') : '#424242',
          border: cat ? `2px solid ${cat.bg}` : '2px solid #bdbdbd',
        }}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4">Product Categories</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, alignItems: 'center' }}>
          {filterChip('ALL', 'All', null)}
          {MARGIN_CATEGORY_ORDER_HIGH_FIRST.map(k => filterChip(k, `${k} - ${MARGIN_CATEGORIES[k].label}`, MARGIN_CATEGORIES[k]))}
          <TextField
            size="small" variant="outlined" placeholder="Search product or brand"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ marginLeft: 'auto', minWidth: 220 }}
          />
        </Box>

        {!products ? (
          <Box style={{ textAlign: 'center', padding: 40 }}><CircularProgress size={28} /></Box>
        ) : grouped.length === 0 ? (
          <Typography style={{ textAlign: 'center', padding: 40, color: '#888' }}>No products found.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f5f5f5' }}>
                <TableCell style={{ fontWeight: 700, width: 56 }} />
                <TableCell style={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Brand</TableCell>
                <TableCell align="right" style={{ fontWeight: 700 }}>MRP</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((g, i) => (g.header
                ? <MarginCategoryHeaderRow key={`h-${g.header.key}`} cat={g.header} count={g.count} colSpan={5} />
                : (
                  <TableRow key={String(g.item._id)} style={{ background: MARGIN_CATEGORIES[g.item.marginCategory]?.rowTint }}>
                    <TableCell>
                      {g.item.image
                        ? <img src={g.item.image} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4 }} />
                        : null}
                    </TableCell>
                    <TableCell style={{ fontSize: 13 }}>{g.item.name}</TableCell>
                    <TableCell style={{ fontSize: 12, color: '#666' }}>{g.item.brand}</TableCell>
                    <TableCell align="right">{fmtMoney(g.item.mrp)}</TableCell>
                    <TableCell>
                      {g.item.isAvailable
                        ? <Chip label="Available" size="small" style={{ background: '#e3f6e6' }} />
                        : <Chip label="Unavailable" size="small" style={{ background: '#fde2e2', color: '#b71c1c' }} />}
                    </TableCell>
                  </TableRow>
                )))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function ProductCategoryButton() {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const role = Number(getDecryptDataFromLocal('setRole'));
  if (!VISIBLE_ROLES.includes(role)) return null;

  return (
    <>
      <Box sx={{ ml: 1, mr: 1 }}>
        <Tooltip title="Product Categories">
          <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <Avatar variant="rounded" className={classes.headerAvatar} onClick={() => setOpen(true)} color="inherit">
              <IconCategory stroke={1.5} size="1.3rem" />
            </Avatar>
          </ButtonBase>
        </Tooltip>
      </Box>
      <ProductCategoryDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
